import { useEffect, useId, useRef } from "react";
import * as d3 from "d3";
import { apiFetch } from "../../api/apiClient";

/* =========================
   CONSTANTS
   ========================= */
const HEIGHT = 520;

const margin = {
  top: 20,
  right: 70,
  bottom: 120,
  left: 80,
};

const ENERGY_COLOR = "#059e00";
const PEAK_COLOR = "#ED7D31";

const tooltipRowStyle = { display: "flex", alignItems: "center", gap: 6 };

function EnergySwatch() {
  return (
    <svg width={20} height={12} aria-hidden style={{ flexShrink: 0 }}>
      <rect width={20} height={12} fill={ENERGY_COLOR} />
    </svg>
  );
}

function PeakSwatch() {
  return (
    <svg width={20} height={12} aria-hidden style={{ flexShrink: 0 }}>
      <line x1={0} y1={6} x2={20} y2={6} stroke={PEAK_COLOR} strokeWidth={3} />
    </svg>
  );
}

const energySwatchHtml =
  `<svg width="20" height="12" aria-hidden="true" style="flex-shrink:0"><rect width="20" height="12" fill="${ENERGY_COLOR}"/></svg>`;
const peakSwatchHtml =
  `<svg width="20" height="12" aria-hidden="true" style="flex-shrink:0"><line x1="0" y1="6" x2="20" y2="6" stroke="${PEAK_COLOR}" stroke-width="3"/></svg>`;

/** Peak power from the imported row (`peak`, or `power` from the aux-load API). */
function peakMw(d) {
  const n = Number(d?.peak ?? d?.power);
  return Number.isFinite(n) ? n : 0;
}

/** Highest finite value in a series. The axis then rounds this up to an even tick. */
function seriesMax(rows, read) {
  const max = d3.max(rows, (d) => {
    const n = Number(read(d));
    return Number.isFinite(n) ? n : NaN;
  });
  return Number.isFinite(max) && max > 0 ? max : 1;
}

/**
 * Round a data maximum up to the next even tick. The returned ticks run from
 * 0 to that ceiling with one constant step.
 */
function evenAxis(dataMax, count = 6) {
  const scale = d3.scaleLinear().domain([0, dataMax]).nice(count);
  const max = scale.domain()[1];
  return { max, ticks: scale.ticks(count) };
}

export default function AuxiliaryLoad({
  portfolioName = "gemini",
  duration = "default",
  startDate = "",
  endDate = "",
  data = [],
  onTooltip,
}) {
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const dataRef = useRef([]);
  const onTooltipRef = useRef(onTooltip);
  onTooltipRef.current = onTooltip;

  // Keyboard navigation: which x-axis point is selected and whether arrow-key
  // driven navigation is currently active. Refs (not state) so the d3 handlers
  // can read/write without forcing React re-renders or redraws.
  const keyboardIndexRef = useRef(0);
  const keyboardModeRef = useRef(false);

  const reactId = useId();
  const gradientId = `barGradient-${reactId.replace(/:/g, "")}`;

  useEffect(() => {
    if (!containerRef.current) return;

    // Tooltip div fallback (only when page-level ChartTooltip isn't used)
    const tooltip = onTooltipRef.current
      ? null
      : d3
          .select("body")
          .append("div")
          .style("position", "absolute")
          .style("left", "0")
          .style("top", "0")
          .style("background", "white")
          .style("border", "1px solid #ccc")
          .style("padding", "6px")
          .style("border-radius", "4px")
          .style("pointer-events", "none")
          .style("box-shadow", "0 0 10px rgba(0,0,0,0.25)")
          .style("opacity", 0);

    // Mutable handle to the latest draw cycle's selection helpers. Populated
    // at the end of every `draw()` call so the effect-scoped keyboard/focus
    // listeners (which outlive a single draw) can drive the most recent
    // chart instance.
    const handlersRef = { current: null };

    function draw() {
      if (!containerRef.current || !dataRef.current.length) return;

      const width = containerRef.current.getBoundingClientRect().width;
      const innerWidth = width - margin.left - margin.right;
      const innerHeight = HEIGHT - margin.top - margin.bottom;

      const svg = d3
        .select(svgRef.current)
        .attr("width", width)
        .attr("height", HEIGHT);

      svg.selectAll("*").remove();

      // Gradient def (stable id per component)
      const defs = svg.append("defs");
      const grad = defs
        .append("linearGradient")
        .attr("id", gradientId)
        .attr("x1", "0")
        .attr("y1", "0")
        .attr("x2", "0")
        .attr("y2", "1");
      grad.append("stop").attr("offset", "0%").attr("stop-color", ENERGY_COLOR);  // purple
      grad.append("stop").attr("offset", "100%").attr("stop-color", "#002fff");    // black

      const g = svg
        .append("g")
        .attr("transform", `translate(${margin.left},${margin.top})`);

      /* =========================
         SCALES
         ========================= */
      const x = d3
        .scaleBand()
        .domain(dataRef.current.map(d => d.date))
        .range([0, innerWidth])
        .padding(0.3);

      const energyAxis = evenAxis(seriesMax(dataRef.current, (d) => d.energy));
      const peakAxis = evenAxis(seriesMax(dataRef.current, peakMw));

      const yLeft = d3
        .scaleLinear()
        .domain([0, energyAxis.max])
        .range([innerHeight, 0]);

      const yRight = d3
        .scaleLinear()
        .domain([0, peakAxis.max])
        .range([innerHeight, 0]);

      /* =========================
         AXES
         ========================= */
      g.append("g")
        .call(
          d3.axisLeft(yLeft)
            .tickValues(energyAxis.ticks)
            .tickFormat(d3.format(",.1f"))
        );

      g.append("g")
        .attr("transform", `translate(${innerWidth},0)`)
        .call(
          d3.axisRight(yRight)
            .tickValues(peakAxis.ticks)
            .tickFormat(d3.format(".3f"))
        );

      g.append("g")
        .attr("transform", `translate(0,${innerHeight})`)
        .call(d3.axisBottom(x))
        .selectAll("text")
        .attr("transform", "rotate(45)")
        .style("text-anchor", "start");

    /* GRID LINES */
        g.append("g")
        .attr("class", "grid")
        .call(
            d3.axisLeft(yLeft)
            .tickValues(energyAxis.ticks)
            .tickSize(-innerWidth)
            .tickFormat("")
        );

        g.selectAll(".grid line")
        .attr("stroke", "#e0e0e0");

        g.selectAll(".grid path").remove();


      /* =========================
         BARS (AUX ENERGY)
         ========================= */
      const BAR_COLOR_ACTIVE = ENERGY_COLOR;
      const barFill = `url(#${gradientId})`;
      const bars = g.selectAll(".bar")
        .data(dataRef.current)
        .enter()
        .append("rect")
        .attr("class", "bar")
        .attr("x", d => x(d.date))
        .attr("y", d => yLeft(d.energy))
        .attr("width", x.bandwidth())
        .attr("height", d => innerHeight - yLeft(d.energy))
        .attr("fill", barFill)
        .style("pointer-events", "none");

      /* =========================
         LINE (AUX PEAK POWER)
         ========================= */
      const line = d3
        .line()
        .x(d => x(d.date) + x.bandwidth() / 2)
        .y(d => yRight(peakMw(d)))
        .curve(d3.curveMonotoneX);

      g.append("path")
        .datum(dataRef.current)
        .attr("fill", "none")
        .attr("stroke", PEAK_COLOR)
        .attr("stroke-width", 3)
        .attr("d", line);

      /* =========================
         PEAK DOTS
         (small by default; enlarged at the active crosshair index)
         ========================= */
      const DOT_R = 3;
      const DOT_R_ACTIVE = 6;

      const peakDots = g.selectAll(".dot-peak")
        .data(dataRef.current)
        .enter()
        .append("circle")
        .attr("class", "dot-peak")
        .attr("cx", d => x(d.date) + x.bandwidth() / 2)
        .attr("cy", d => yRight(peakMw(d)))
        .attr("r", DOT_R)
        .attr("fill", PEAK_COLOR)
        .style("pointer-events", "none");

      /* =========================
         CROSSHAIR + TOOLTIP
         Snap-by-x to nearest x-axis point; tooltip anchors to the snapped
         bar's top edge (centered on the bar) rather than following the
         cursor, for stable hover behavior.
         ========================= */
      const gCrosshair = g.append("g").attr("class", "crosshair");
      const gOverlay = g.append("g").attr("class", "overlay");

      const crosshair = gCrosshair.append("line")
        .attr("stroke", "#666")
        .attr("y1", 0)
        .attr("y2", innerHeight)
        .style("opacity", 0)
        .style("pointer-events", "none");

      const centers = dataRef.current.map(d => x(d.date) + x.bandwidth() / 2);

      const resetDots = () => {
        peakDots.attr("r", DOT_R);
      };

      const resetBars = () => {
        bars.attr("fill", barFill);
      };

      // Anchor the tooltip to the snapped bar (centered on the bar, just
      // above its top) instead of the cursor. The fallback <div> tooltip
      // uses page coordinates; the page-level callback uses viewport
      // coordinates so it can be positioned by a fixed-position element.
      const showTooltipForDatum = (d, centerX) => {
        const barTopY = yLeft(d.energy);
        const svgEl = svgRef.current;
        if (!svgEl) return;
        const rect = svgEl.getBoundingClientRect();

        const tip = onTooltipRef.current;
        if (tip) {
          tip({
            visible: true,
            x: rect.left + margin.left + centerX,
            y: rect.top + margin.top + barTopY - 12,
            content: (
              <>
                <div>
                  <strong>DATE:</strong> {d.date}
                </div>
                <div style={tooltipRowStyle}>
                  <EnergySwatch />
                  <span>
                    <strong>ENERGY:</strong> {d.energy} MWh
                  </span>
                </div>
                <div style={tooltipRowStyle}>
                  <PeakSwatch />
                  <span>
                    <strong>PEAK:</strong> {peakMw(d)} MW
                  </span>
                </div>
              </>
            ),
          });
        } else if (tooltip) {
          tooltip
            .style("opacity", 1)
            .html(
              `<div><strong>DATE:</strong> ${d.date}</div>` +
              `<div style="display:flex;align-items:center;gap:6px">${energySwatchHtml}<span><strong>ENERGY:</strong> ${d.energy} MWh</span></div>` +
              `<div style="display:flex;align-items:center;gap:6px">${peakSwatchHtml}<span><strong>PEAK:</strong> ${peakMw(d)} MW</span></div>`
            )
            .style(
              "left",
              `${rect.left + window.scrollX + margin.left + centerX + 10}px`
            )
            .style(
              "top",
              `${rect.top + window.scrollY + margin.top + barTopY - 30}px`
            );
        }
      };

      const showSelectionByIndex = (idx) => {
        if (!centers.length) return;
        const clamped = Math.max(0, Math.min(centers.length - 1, idx));
        keyboardIndexRef.current = clamped;
        const d = dataRef.current[clamped];
        if (!d) return;

        crosshair
          .attr("x1", centers[clamped])
          .attr("x2", centers[clamped])
          .style("opacity", 1);

        resetDots();
        peakDots.filter((_, i) => i === clamped).attr("r", DOT_R_ACTIVE);

        resetBars();
        bars.filter((_, i) => i === clamped).attr("fill", BAR_COLOR_ACTIVE);

        showTooltipForDatum(d, centers[clamped]);
      };

      const hideSelection = () => {
        crosshair.style("opacity", 0);
        resetDots();
        resetBars();
        const tip = onTooltipRef.current;
        if (tip) {
          tip((t) => ({ ...t, visible: false }));
        } else {
          tooltip?.style("opacity", 0);
        }
      };

      gOverlay
        .append("rect")
        .attr("width", innerWidth)
        .attr("height", innerHeight)
        .attr("fill", "transparent")
        .style("cursor", "crosshair")
        .on("mousemove", event => {
          if (!centers.length) return;
          // Any real mouse movement on the chart kicks us out of keyboard
          // navigation mode and back into mouse-follow mode.
          keyboardModeRef.current = false;
          const [mx] = d3.pointer(event);
          const idx = Math.max(
            0,
            Math.min(centers.length - 1, d3.bisectCenter(centers, mx))
          );
          showSelectionByIndex(idx);
        })
        .on("mouseleave", () => {
          // If the chart still has keyboard focus when the mouse leaves,
          // fall back to keyboard-navigation mode so the user can keep
          // arrow-keying without losing the visible selection.
          if (svgRef.current && document.activeElement === svgRef.current) {
            keyboardModeRef.current = true;
            showSelectionByIndex(keyboardIndexRef.current);
          } else {
            hideSelection();
          }
        });

      /* =========================
         TITLES & AXIS LABELS
         ========================= */
      g.append("text")
        .attr("transform", "rotate(-90)")
        .attr("x", -innerHeight / 2)
        .attr("y", -60)
        .attr("text-anchor", "middle")
        .style("font-size", "14px")
        .style("font-weight", "bold")
        .text("Aux Energy, MWh");

      g.append("text")
        .attr("transform", "rotate(-90)")
        .attr("x", -innerHeight / 2)
        .attr("y", innerWidth + 60)
        .attr("text-anchor", "middle")
        .style("font-size", "14px")
        .style("font-weight", "bold")
        .text("Aux Peak Power, MW");

      /* =========================
         LEGEND
         ========================= */
      const legend = svg
        .append("g")
        .attr(
          "transform",
          `translate(${width / 2 - 180},${HEIGHT - 40})`
        );

      legend.append("rect")
        .attr("width", 20)
        .attr("height", 12)
        .attr("fill", ENERGY_COLOR);

      legend.append("text")
        .attr("x", 26)
        .attr("y", 10)
        .style("font-weight", "bold")
        .text("Aux Energy, MWh");

      legend.append("line")
        .attr("x1", 170)
        .attr("x2", 190)
        .attr("y1", 6)
        .attr("y2", 6)
        .attr("stroke", PEAK_COLOR)
        .attr("stroke-width", 3);

      legend.append("text")
        .attr("x", 200)
        .attr("y", 10)
        .style("font-weight", "bold")
        .text("Aux Peak Power, MW");

      // Publish this draw cycle's selection helpers so the effect-scoped
      // keyboard/focus listeners (which outlive a single draw) can drive
      // the most recent chart instance.
      handlersRef.current = {
        centersLength: centers.length,
        showSelectionByIndex,
        hideSelection,
      };

      // If the chart was already focused when this draw ran (e.g. resize
      // fired while the user was navigating with the keyboard), re-render
      // the selection so the crosshair persists across redraws.
      if (
        keyboardModeRef.current &&
        svgRef.current &&
        document.activeElement === svgRef.current
      ) {
        showSelectionByIndex(keyboardIndexRef.current);
      }
    }

    let cancelled = false;

    const load = async () => {
      if (Array.isArray(data) && data.length > 0) {
        dataRef.current = data;
        draw();
        return;
      }

      if (!portfolioName) {
        dataRef.current = [];
        draw();
        return;
      }

      try {
        const query = new URLSearchParams();
        if (startDate && endDate) {
          query.set("start", startDate);
          query.set("end", endDate);
        }
        const suffix = query.toString() ? `?${query}` : "";
        const payload = await apiFetch(`/api/graph/portfolio/${portfolioName}/insights/${encodeURIComponent(duration)}/aux-load${suffix}`);
        if (cancelled) return;
        dataRef.current = payload?.results || [];
        draw();
      } catch (err) {
        console.warn("Auxiliary load API fetch failed:", err);
      }
    };

    load();

    /* =========================
       RESIZE HANDLING
       ========================= */
    const observer = new ResizeObserver(draw);
    observer.observe(containerRef.current);

    /* =========================
       KEYBOARD NAVIGATION
       Arrow-key step through x-axis points while the <svg> has focus.
       Listeners are attached once per effect run and read from
       `handlersRef` so they always drive the most recent draw cycle.
       ========================= */
    const svgEl = svgRef.current;

    const handleKeyDown = (e) => {
      const h = handlersRef.current;
      if (!keyboardModeRef.current || !h || !h.centersLength) return;
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        if (keyboardIndexRef.current > 0) {
          h.showSelectionByIndex(keyboardIndexRef.current - 1);
        }
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        if (keyboardIndexRef.current < h.centersLength - 1) {
          h.showSelectionByIndex(keyboardIndexRef.current + 1);
        }
      }
    };

    const handleFocus = () => {
      const h = handlersRef.current;
      if (!h || !h.centersLength) return;
      keyboardModeRef.current = true;
      h.showSelectionByIndex(keyboardIndexRef.current);
    };

    const handleBlur = () => {
      keyboardModeRef.current = false;
      handlersRef.current?.hideSelection();
    };

    svgEl?.addEventListener("keydown", handleKeyDown);
    svgEl?.addEventListener("focus", handleFocus);
    svgEl?.addEventListener("blur", handleBlur);

    return () => {
      cancelled = true;
      observer.disconnect();
      tooltip?.remove?.();
      svgEl?.removeEventListener("keydown", handleKeyDown);
      svgEl?.removeEventListener("focus", handleFocus);
      svgEl?.removeEventListener("blur", handleBlur);
    };
  }, [data, portfolioName, duration, startDate, endDate, gradientId]);

  return (
    <div ref={containerRef} style={{ width: "100%", minWidth: "0" }}>
      <svg
        ref={svgRef}
        tabIndex={0}
        role="img"
        aria-label="Auxiliary load chart. Use left and right arrow keys to navigate between dates."
        style={{ display: "block" }}
      />
    </div>
  );
}
